import React, { useCallback, useMemo, useState } from "react";
import { Alert } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import GenericDialog from "@components/GenericDialog/GenericDialog";
import { normalizeDrawerText } from "@components/SideDrawer/SideDrawer.schema";
import { getSideDrawerStyles } from "@components/SideDrawer/SideDrawer.styles";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { SIDE_DRAWER_MAX_WIDTH } from "@constants/layout.style";
import { useNotification } from "@context/NotificationContext";
import type { BulkSecurityClearanceResponse } from "@services/security-clearance/types";
import {
  bulkCompleteSecurityClearance,
  bulkPermanentlyRejectSecurityClearance,
  bulkSuspendSecurityClearance,
  SECURITY_CLEARANCE_BULK_MAX,
} from "@services/securityClearanceApi";
import { useMutation } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { useTranslation } from "react-i18next";
import {
  BULK_SC_SERVICE,
  notifyBulkDrawerError,
  notifyBulkDrawerOutcome,
  notifyBulkSelectionLimit,
  validateBulkDrawerReason,
  type BulkSecurityClearanceRow,
  type SecurityClearanceError,
} from "./bulkSecurityClearanceDrawerShared";
import BulkSecurityClearanceReasonField from "./BulkSecurityClearanceReasonField";

export type BulkReasonMode = "resubmit" | "permanentReject" | "suspend";

const getBulkReasonTransactionAction = (mode: BulkReasonMode): string => {
  if (mode === "resubmit") return "bulk_resubmit_security_clearances";
  if (mode === "permanentReject") return "bulk_permanently_reject_security_clearances";
  return "bulk_suspend_security_clearances";
};

interface BulkReasonSecurityClearanceDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  mode: BulkReasonMode;
  selectedRows: BulkSecurityClearanceRow[];
  onSuccess?: () => void;
}

type ModeConfig = {
  testIdPrefix: string;
  drawerTitle: string;
  drawerMessage: string;
  reasonLabel: string;
  reasonPlaceholder: string;
  reasonRequiredLabel: string;
  submitLabel: string;
  confirmTitle: string;
  confirmMessage: string;
  successTitle: string;
  partialTitle: string;
  partialDetailKey: string;
  partialDetailDefault: string;
  request: (
    ids: string[],
    reason: string,
    transactionId?: string,
  ) => Promise<BulkSecurityClearanceResponse>;
};

const BulkReasonSecurityClearanceDrawer: React.FC<BulkReasonSecurityClearanceDrawerProps> = ({
  isOpen,
  onClose,
  mode,
  selectedRows,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const styles = getSideDrawerStyles(theme);
  const { notify } = useNotification();

  const [reason, setReason] = useState("");
  const [errors, setErrors] = useState<{ reason?: string }>({});
  const [error] = useState<null | string>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const config = useMemo<ModeConfig>(() => {
    if (mode === "resubmit") {
      return {
        testIdPrefix: "bulk-resubmit-security-clearance",
        drawerTitle: t("security-clearance.bulk-resubmit-drawer-title", {
          defaultValue: "Bulk Resubmit Security Clearance",
        }),
        drawerMessage: t("security-clearance.bulk-resubmit-drawer-message", {
          defaultValue: "You are about to bulk resubmit Security Clearance",
        }),
        reasonLabel: t("security-clearance.resubmission-reason", {
          defaultValue: "Resubmission reason",
        }),
        reasonPlaceholder: t("security-clearance.resubmission-reason-placeholder", {
          defaultValue: "Enter resubmission reason",
        }),
        reasonRequiredLabel: t("security-clearance.resubmission-reason-will-apply-all", {
          defaultValue: "Resubmission reason (will apply to all requests)",
        }),
        submitLabel: t("security-clearance.bulk-resubmit-button", {
          defaultValue: "Bulk Resubmit",
        }),
        confirmTitle: t("security-clearance.bulk-resubmit-confirm-title", {
          defaultValue: "Confirm bulk resubmission",
        }),
        confirmMessage: t("security-clearance.bulk-resubmit-confirm-message", {
          defaultValue:
            "You are about to send {{count}} security clearance request(s) for resubmission.",
          count: selectedRows.length,
        }),
        successTitle: t("security-clearance.security-clearance-request-rejected", {
          defaultValue: "Security Clearance request rejected!",
        }),
        partialTitle: t("security-clearance.bulk-resubmit-partial", {
          defaultValue: "Bulk resubmission completed with errors",
        }),
        partialDetailKey: "security-clearance.bulk-resubmit-partial-detail",
        partialDetailDefault: "{{success}} sent for resubmission, {{failed}} failed.",
        request: (ids, trimmedReason, transactionId) =>
          bulkCompleteSecurityClearance(
            BULK_SC_SERVICE,
            {
              items: ids.map((id) => ({ securityClearanceId: id })),
              taskDecision: { status: "RESUBMIT", reason: trimmedReason },
            },
            ...(transactionId ? [transactionId] : []),
          ),
      };
    }

    if (mode === "permanentReject") {
      return {
        testIdPrefix: "bulk-permanently-reject-security-clearance",
        drawerTitle: t("security-clearance.bulk-permanently-reject-drawer-title", {
          defaultValue: "Bulk Permanently Reject Security Clearance",
        }),
        drawerMessage: t("security-clearance.bulk-permanently-reject-drawer-message", {
          defaultValue: "You are about to bulk permanently reject Security Clearance",
        }),
        reasonLabel: t("security-clearance.rejection-reason", {
          defaultValue: "Rejection reason",
        }),
        reasonPlaceholder: t("security-clearance.rejection-reason-placeholder", {
          defaultValue: "Enter rejection reason",
        }),
        reasonRequiredLabel: t("security-clearance.rejection-reason-will-apply-all", {
          defaultValue: "Rejection reason (will apply to all requests)",
        }),
        submitLabel: t("security-clearance.bulk-permanently-reject-button", {
          defaultValue: "Permanently reject",
        }),
        confirmTitle: t("security-clearance.bulk-permanently-reject-confirm-title", {
          defaultValue: "Confirm bulk permanent rejection",
        }),
        confirmMessage: t("security-clearance.bulk-permanently-reject-confirm-message", {
          defaultValue:
            "You are about to permanently reject {{count}} security clearance request(s).",
          count: selectedRows.length,
        }),
        successTitle: t("security-clearance.security-clearance-request-rejected", {
          defaultValue: "Security clearance request rejected!",
        }),
        partialTitle: t("security-clearance.bulk-permanently-reject-partial", {
          defaultValue: "Bulk permanent rejection completed with errors",
        }),
        partialDetailKey: "security-clearance.bulk-permanently-reject-partial-detail",
        partialDetailDefault: "{{success}} processed, {{failed}} failed.",
        request: (ids, trimmedReason, transactionId) =>
          bulkPermanentlyRejectSecurityClearance(
            BULK_SC_SERVICE,
            {
              securityClearanceIds: ids,
              reason: trimmedReason,
            },
            ...(transactionId ? [transactionId] : []),
          ),
      };
    }

    return {
      testIdPrefix: "bulk-suspend-security-clearance",
      drawerTitle: t("security-clearance.bulk-suspend-drawer-title", {
        defaultValue: "Bulk Suspend Security Clearance",
      }),
      drawerMessage: t("security-clearance.bulk-suspend-drawer-message", {
        defaultValue: "You are about to bulk suspend Security Clearance",
      }),
      reasonLabel: t("security-clearance.suspension-reason", {
        defaultValue: "Suspension reason",
      }),
      reasonPlaceholder: t("security-clearance.suspension-reason-placeholder", {
        defaultValue: "Enter suspension reason",
      }),
      reasonRequiredLabel: t("security-clearance.suspension-reason-will-apply-all", {
        defaultValue: "Suspension reason (will apply to all requests)",
      }),
      submitLabel: t("security-clearance.bulk-suspend-button", { defaultValue: "Suspend" }),
      confirmTitle: t("security-clearance.bulk-suspend-confirm-title", {
        defaultValue: "Confirm bulk suspension",
      }),
      confirmMessage: t("security-clearance.bulk-suspend-confirm-message", {
        defaultValue: "You are about to suspend {{count}} security clearance request(s).",
        count: selectedRows.length,
      }),
      successTitle: t("security-clearance.security-clearance-request-suspended", {
        defaultValue: "Security Clearance has been Suspended!",
      }),
      partialTitle: t("security-clearance.bulk-suspend-partial", {
        defaultValue: "Bulk suspension completed with errors",
      }),
      partialDetailKey: "security-clearance.bulk-suspend-partial-detail",
      partialDetailDefault: "{{success}} processed, {{failed}} failed.",
      request: (ids, trimmedReason, transactionId) =>
        bulkSuspendSecurityClearance(
          BULK_SC_SERVICE,
          {
            securityClearanceIds: ids,
            reason: trimmedReason,
          },
          ...(transactionId ? [transactionId] : []),
        ),
    };
  }, [mode, selectedRows.length, t]);

  const resetFormValues = useCallback(() => {
    setReason("");
    setErrors({});
    setShowConfirmDialog(false);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    onClose();
    resetFormValues();
  }, [onClose, resetFormValues]);

  const bulkMutation = useMutation({
    mutationFn: async () =>
      runTransaction(
        {
          module: "security_clearance",
          screen: "Security Clearance Bulk Reason",
          action: getBulkReasonTransactionAction(mode),
          entityType: "security_clearance",
        },
        async (transactionId) => {
          const ids = selectedRows.map((row) => row.id);
          const trimmedReason = normalizeDrawerText(reason);
          return config.request(ids, trimmedReason, ...(transactionId ? [transactionId] : []));
        },
      ),
    onSuccess: async (data) => {
      onSuccess?.();
      onClose();
      resetFormValues();
      notifyBulkDrawerOutcome(notify, t, data, selectedRows.length, {
        partialTitle: config.partialTitle,
        partialDetailKey: config.partialDetailKey,
        partialDetailDefault: config.partialDetailDefault,
        successTitle: config.successTitle,
      });
    },
    onError: async (err: SecurityClearanceError) => {
      setShowConfirmDialog(false);
      notifyBulkDrawerError(notify, t, err);
      if (err.status === 409) {
        handleCloseDrawer();
      }
    },
  });

  const validateReason = useCallback(
    (value: string) => validateBulkDrawerReason(value, config.reasonLabel, t),
    [config.reasonLabel, t],
  );

  const handleSubmit = useCallback(() => {
    if (selectedRows.length > SECURITY_CLEARANCE_BULK_MAX) {
      notifyBulkSelectionLimit(notify, t);
      return;
    }

    const trimmedReason = normalizeDrawerText(reason);
    setReason(trimmedReason);

    const reasonError = validateReason(trimmedReason);
    if (reasonError) {
      setErrors({ reason: reasonError });
      return;
    }

    setErrors({});
    setShowConfirmDialog(true);
  }, [notify, reason, selectedRows.length, t, validateReason]);

  const handleConfirmDialogConfirm = useCallback(() => {
    bulkMutation.mutate();
  }, [bulkMutation]);

  const handleConfirmDialogClose = useCallback(() => {
    setShowConfirmDialog(false);
  }, []);

  if (selectedRows.length === 0) return null;

  return (
    <div
      data-testid={`${config.testIdPrefix}-drawer-form`}
      className="max-h-screen overflow-y-auto"
    >
      <SideDrawer
        isOpen={isOpen}
        title={config.drawerTitle}
        onClose={handleCloseDrawer}
        submitLabel={config.submitLabel}
        onSubmit={handleSubmit}
        isSubmitting={bulkMutation.isPending}
        width={SIDE_DRAWER_MAX_WIDTH}
      >
        <div data-testid={`${config.testIdPrefix}-drawer-content`} className="flex-1 w-full">
          <div className="px-6">
            <div className="mt-0 mb-2">
              <p data-testid={`${config.testIdPrefix}-form-title`} style={styles.descriptionText}>
                {config.drawerMessage}
              </p>
              {selectedRows.map((row) => (
                <span key={row.id} style={styles.idText}>
                  #{row.serialNo}.
                  <br />
                </span>
              ))}
            </div>
            <BulkSecurityClearanceReasonField
              theme={theme}
              reason={reason}
              reasonLabel={config.reasonLabel}
              reasonRequiredLabel={config.reasonRequiredLabel}
              reasonPlaceholder={config.reasonPlaceholder}
              reasonError={errors.reason}
              testIdPrefix={config.testIdPrefix}
              onReasonChange={setReason}
              onReasonError={(reasonError) => setErrors({ reason: reasonError })}
              validateReason={validateReason}
              t={t}
            />
          </div>
        </div>
      </SideDrawer>
      <GenericDialog
        open={showConfirmDialog}
        title={config.confirmTitle}
        elementContent={
          <>
            {config.confirmMessage}
            <br />
            {t("security-clearance.bulk-confirm-question", {
              defaultValue: "Are you sure you want to proceed?",
            })}
          </>
        }
        cancelButtonText={t("common.cancel")}
        confirmButtonText={t("common.confirm")}
        handleCloseDialog={handleConfirmDialogClose}
        confirmAction={handleConfirmDialogConfirm}
        testId={`${config.testIdPrefix}-confirm-dialog`}
        isInsideDrawer
      />
      {error ? <Alert severity="error">{error}</Alert> : null}
    </div>
  );
};

export default BulkReasonSecurityClearanceDrawer;
export type { BulkSecurityClearanceRow };
